import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const NewsletterSettings = () => {
  const [settings, setSettings] = useState({
    newsletter_discount_enabled: true,
    discount_percentage: 20,
    code_validity_days: 30,
    default_code: 'WELCOME20',
    auto_generate_codes: true
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    try {
      const saved = localStorage.getItem('newsletter-discount-settings');
      if (saved) {
        setSettings(JSON.parse(saved));
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoading(false);
    }
  };

  const saveSettings = () => {
    try {
      localStorage.setItem('newsletter-discount-settings', JSON.stringify(settings));
      toast.success('Einstellungen gespeichert!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#E8E0D5' }}>
        <p className="text-center">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Newsletter & Rabatte</h1>
            <p className="text-sm text-gray-600 mt-1">Verwalten Sie Newsletter-Rabatte und Codes</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Main Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle>Newsletter-Rabatt Einstellungen</CardTitle>
              <CardDescription>
                Aktivieren Sie Rabattcodes für Newsletter-Anmeldungen und legen Sie die Konditionen fest
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-1">Newsletter-Rabatte aktivieren</h3>
                  <p className="text-sm text-gray-600">
                    Wenn aktiviert, erhalten neue Newsletter-Abonnenten automatisch einen Rabattcode
                  </p>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, newsletter_discount_enabled: !settings.newsletter_discount_enabled })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    settings.newsletter_discount_enabled
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                  }`}
                >
                  {settings.newsletter_discount_enabled ? (
                    <>
                      <ToggleRight className="h-5 w-5" />
                      Aktiv
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="h-5 w-5" />
                      Inaktiv
                    </>
                  )}
                </button>
              </div>

              {/* Discount Percentage */}
              <div>
                <Label htmlFor="discount-percentage" className="text-base font-semibold">
                  Rabatt-Prozentsatz
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Wie viel Prozent Rabatt sollen Newsletter-Abonnenten erhalten?
                </p>
                <div className="flex items-center gap-4">
                  <Input
                    id="discount-percentage"
                    type="number"
                    min="1"
                    max="100"
                    value={settings.discount_percentage}
                    onChange={(e) => setSettings({ ...settings, discount_percentage: parseInt(e.target.value) || 0 })}
                    className="max-w-xs"
                    disabled={!settings.newsletter_discount_enabled}
                  />
                  <span className="text-xl font-semibold text-gray-700">%</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Empfohlen: 10-25% für neue Kunden
                </p>
              </div>

              {/* Code Validity */}
              <div>
                <Label htmlFor="validity-days" className="text-base font-semibold">
                  Gültigkeit des Codes
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Wie lange soll der Rabattcode gültig sein?
                </p>
                <Select
                  value={settings.code_validity_days.toString()}
                  onValueChange={(value) => setSettings({ ...settings, code_validity_days: parseInt(value) })}
                  disabled={!settings.newsletter_discount_enabled}
                >
                  <SelectTrigger className="max-w-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 Tage</SelectItem>
                    <SelectItem value="14">14 Tage (2 Wochen)</SelectItem>
                    <SelectItem value="30">30 Tage (1 Monat)</SelectItem>
                    <SelectItem value="60">60 Tage (2 Monate)</SelectItem>
                    <SelectItem value="90">90 Tage (3 Monate)</SelectItem>
                    <SelectItem value="180">180 Tage (6 Monate)</SelectItem>
                    <SelectItem value="365">365 Tage (1 Jahr)</SelectItem>
                    <SelectItem value="0">Unbegrenzt</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-2">
                  Nach Ablauf kann der Code nicht mehr verwendet werden
                </p>
              </div>

              {/* Default Code */}
              <div>
                <Label htmlFor="default-code" className="text-base font-semibold">
                  Standard-Rabattcode
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Dieser Code wird auf der Homepage angezeigt (wenn keine individuellen Codes generiert werden)
                </p>
                <Input
                  id="default-code"
                  type="text"
                  value={settings.default_code}
                  onChange={(e) => setSettings({ ...settings, default_code: e.target.value.toUpperCase() })}
                  placeholder="z.B. WELCOME20"
                  className="max-w-xs"
                  disabled={!settings.newsletter_discount_enabled}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Nur Großbuchstaben und Zahlen, keine Leerzeichen
                </p>
              </div>

              {/* Auto-generate Codes */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <input
                  type="checkbox"
                  id="auto-generate"
                  checked={settings.auto_generate_codes}
                  onChange={(e) => setSettings({ ...settings, auto_generate_codes: e.target.checked })}
                  className="mt-1"
                  disabled={!settings.newsletter_discount_enabled}
                />
                <div className="flex-1">
                  <Label htmlFor="auto-generate" className="font-semibold cursor-pointer">
                    Individuelle Codes automatisch generieren
                  </Label>
                  <p className="text-sm text-gray-700 mt-1">
                    Wenn aktiviert, erhält jeder Newsletter-Abonnent einen einzigartigen Code. 
                    Wenn deaktiviert, wird der Standard-Code für alle verwendet.
                  </p>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={saveSettings}
                  className="bg-[#C4A084] hover:bg-[#B89968]"
                  size="lg"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Einstellungen speichern
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card>
            <CardHeader>
              <CardTitle>Vorschau</CardTitle>
              <CardDescription>So wird es Ihren Kunden angezeigt</CardDescription>
            </CardHeader>
            <CardContent>
              {settings.newsletter_discount_enabled ? (
                <div className="space-y-4">
                  {/* Newsletter Popup Preview */}
                  <div className="border-2 border-gray-300 rounded-lg p-6 bg-white">
                    <div className="text-center">
                      <div className="inline-block px-6 py-2 rounded-full mb-3 shadow-md" style={{
                        background: 'linear-gradient(135deg, #C4A084 0%, #B89968 100%)'
                      }}>
                        <p className="text-xl font-bold text-white">
                          {settings.discount_percentage}% Willkommens-Rabatt
                        </p>
                      </div>
                      <p className="text-sm text-gray-600">
                        {settings.auto_generate_codes 
                          ? 'Erhalten Sie einen einzigartigen Rabattcode per E-Mail'
                          : `Ihr Code: ${settings.default_code}`
                        }
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Gültig für {settings.code_validity_days === 0 ? 'unbegrenzte Zeit' : `${settings.code_validity_days} Tage`}
                      </p>
                    </div>
                  </div>

                  {/* Homepage Code Display Preview */}
                  {!settings.auto_generate_codes && (
                    <div className="border-2 border-gray-300 rounded-lg p-6 bg-gradient-to-br from-[#E8E0D5] to-[#D4C4B0]">
                      <div className="text-center">
                        <h3 className="text-2xl font-bold mb-2 text-gray-800">
                          {settings.discount_percentage}% Rabatt für dich!
                        </h3>
                        <p className="text-sm text-gray-700 mb-3">
                          Dein persönlicher Code:
                        </p>
                        <div className="inline-block px-8 py-4 rounded-lg bg-gradient-to-r from-[#8B7355] to-[#6D5A47]">
                          <code className="text-3xl font-bold text-white tracking-widest">
                            {settings.default_code}
                          </code>
                        </div>
                        <p className="text-xs text-gray-600 mt-3">
                          ✨ Code beim Checkout eingeben & sparen!
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg font-semibold mb-2">Newsletter-Rabatte sind deaktiviert</p>
                  <p className="text-sm">Aktivieren Sie die Funktion oben, um eine Vorschau zu sehen</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="border-2 border-blue-300 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="text-blue-600 text-2xl">💡</div>
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">Hinweise</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>Individuelle Codes:</strong> Jeder Kunde erhält einen einzigartigen Code, der nur einmal verwendet werden kann</li>
                    <li>• <strong>Standard-Code:</strong> Ein Code für alle - kann mehrfach verwendet werden</li>
                    <li>• <strong>Gültigkeit:</strong> Abgelaufene Codes werden automatisch abgelehnt beim Checkout</li>
                    <li>• <strong>Deaktivieren:</strong> Bestehende Codes bleiben gültig, aber es werden keine neuen erstellt</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NewsletterSettings;
